import {objectHelpers} from './object.helpers';

/**
 * Collection of URL, hash, and navigation location utility helpers.
 * @example
 * import { locationHelpers } from './location.helpers';
 * const filename = locationHelpers.getUrlFilename('https://example.com/files/report.pdf');
 */
export const locationHelpers = {
  /**
   * Extracts the filename from a URL and truncates it if it exceeds the maximum length.
   * @param {string} url - Full URL from which to extract the filename.
   * @param {number} [fileNameMaxLength=18] - Maximum number of characters for the base filename before truncation.
   * @returns {string} Extracted (and possibly truncated) filename.
   * @example
   * locationHelpers.getUrlFilename('https://example.com/files/my-long-report.pdf', 10);
   * // 'my-long-re...pdf'
   */
  getUrlFilename: (url, fileNameMaxLength = 18) => {
    let filename = url.replace(/#.*$/, '').replace(/\?.*$/, '').split('/');
    filename = filename[filename.length - 1];
    if (filename.length > fileNameMaxLength) {
      const splitFileName = filename.split('.');
      filename = `${splitFileName[0].slice(0, fileNameMaxLength)}...${splitFileName[1]}`;
    }
    return filename;
  },
  /**
   * Tests whether a given string is a valid URL using a regular expression.
   * @param {string} url - String to validate as a URL.
   * @returns {boolean} True if the string matches the URL pattern, false otherwise.
   * @example
   * locationHelpers.isValidUrl('https://example.com/path'); // true
   * locationHelpers.isValidUrl('not-a-url'); // false
   */
  isValidUrl: (url) => {
    const regex = new RegExp('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?');
    return regex.test(url);
  },
  /**
   * Extracts the hash string from a location object, stripping the leading '#' character.
   * @param {Location} location - Browser location object containing a hash property.
   * @returns {string|undefined} Hash string without '#', or undefined if no hash is present.
   * @example
   * locationHelpers.getLocationHash({ hash: '#section1' }); // 'section1'
   */
  getLocationHash: (location) => {
    let {hash} = location;
    if (hash) {
      hash = hash.replace('#', '');
    }
    return hash;
  },
  /**
   * Extracts the hash value from a location object, removing the '#' prefix and any query string portion.
   * @param {Location|null} location - Browser location object, or null/undefined.
   * @returns {string|null} Hash string without '#' and without query params, or null if location is falsy.
   * @example
   * locationHelpers.getHash({ hash: '#modal?foo=bar' }); // 'modal'
   */
  getHash: (location) => {
    let hash = null;
    if (location) {
      hash = location.hash.replace('#', '').split('?')[0];
    }
    return hash;
  },
  /**
   * Filters out empty, null, and undefined values from a params object, returning only non-empty entries.
   * @param {Object} [params={}] - Object of query parameter key-value pairs to filter.
   * @returns {Object} New object containing only the params whose values are non-empty.
   * @example
   * locationHelpers.getSearchParams({ page: 1, search: '', status: null }); // { page: 1 }
   */
  getSearchParams: (params = {}) => {
    let value;
    let query = {};
    Object.keys(params).forEach(key => {
      value = params[key];
      if (!['', null, undefined].includes(value)) {
        query = { ...query, [key]: value };
      }
    });
    return query;
  },
  /**
   * Parses search query parameters from a location object, supporting both the search string and hash-embedded query strings.
   * @param {Location} location - Browser location object with hash and search properties.
   * @returns {Object} Plain object mapping query parameter keys to their string values.
   * @example
   * locationHelpers.getSearchParamsFromLocation({ hash: '#modal?foo=bar', search: '' }); // { foo: 'bar' }
   */
  getSearchParamsFromLocation: (location) => {
    const {hash, search} = location;
    let query = search;
    if (hash) {
      const spittedUrl = hash.split('?');
      query = spittedUrl.length > 1 ? spittedUrl[1] : '';
    }
    return Object.fromEntries(new URLSearchParams(query));
  },
  /**
   * Determines whether the active path matches any route in a list, supporting dynamic UUID segments.
   * @param {string} activePath - The current URL path to test.
   * @param {string[]} [paths=[]] - List of route pattern strings to match against.
   * @returns {boolean} True if the active path matches at least one route in the list.
   * @example
   * locationHelpers.isActivePathInList('/users/abc-123', ['/users/:id']); // true
   */
  isActivePathInList: (activePath, paths = []) => {
    if (!activePath) return false;

    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const uuidSimplifiedLength = 32;
    const activeSegments = activePath.split('/');

    return paths.some((route) => {
      if (route === activePath || activePath.startsWith(route)) return true;

      const routeSegments = route.split('/');
      if (routeSegments.length !== activeSegments.length) return false;

      // Normalize dynamic segments (UUID placeholders)
      const normalizedRoute = routeSegments.map((segment, index) =>
        segment.startsWith(':') && (uuidPattern.test(activeSegments[index]) || activeSegments[index].length === uuidSimplifiedLength)
          ? ''
          : segment
      );

      const normalizedActive = activeSegments.map((segment, index) =>
        routeSegments[index].startsWith(':') ? '' : segment
      );

      return objectHelpers.arraysIsEqual(normalizedRoute, normalizedActive);
    });
  },
  /**
   * Returns the full current URL string composed of pathname, hash, and search.
   * @param {Location} location - Browser location object with pathname, hash, and search properties.
   * @returns {string} Concatenated URL string.
   * @example
   * locationHelpers.getFullPath(location); // '/invoices#modal?page=2'
   */
  getFullPath: (location) => location.pathname + location.hash + location.search,
  /**
   * Merges filter parameters into an existing URL search string, replacing existing keys and appending array values.
   * @param {string} [initialSearch=''] - Existing query string (with or without leading '?').
   * @param {Object} [filterParams={}] - Key-value pairs to set; array values append multiple entries for the same key.
   * @returns {string} Updated query string without a leading '?'.
   * @example
   * locationHelpers.buildSearchWithParams('?page=1', { status: ['active', 'pending'] });
   * // 'page=1&status=active&status=pending'
   */
  buildSearchWithParams(initialSearch = '', filterParams = {}) {
    const params = new URLSearchParams(initialSearch ? initialSearch.replace(/^\?/, '') : '');
    const formatValue = (value) => String(value).replace(/\s+/g, '');

    Object.entries(filterParams).forEach(([key, value]) => {
      params.delete(key);
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, formatValue(v)));
      } else {
        params.set(key, formatValue(value));
      }
    });

    return params.toString();
  }
}
